import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, from, of, switchMap, tap, throwError } from 'rxjs';

import { CredentialsService } from './credentials.service';
import { DeviceService } from '../@shared/device.service';
import { RuntimeConfigService } from '../@shared/runtime-config.service';
import { PasskeyService } from './passkey.service';
import {
  LoginControllerService,
  LoginRequest,
  LogoutControllerService,
  AuthRefreshTokenRequest,
} from 'projects/api-swagger-client/src';

export interface LoginContext extends LoginRequest {
  remember: string;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private credentialsService: CredentialsService,
    private loginControllerService: LoginControllerService,
    private logoutControllerService: LogoutControllerService,
    private deviceService: DeviceService,
    private passkeyService: PasskeyService,
    private runtimeConfigService: RuntimeConfigService
  ) {}

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(loginRequest: LoginContext): Observable<any> {
    const { remember, ...loginData } = loginRequest;
    // Fase 2: adjuntar identidad de dispositivo (deviceId/platform/deviceName) para
    // que el backend registre la sesión con su origen. Campos opcionales en el DTO.
    const body = { ...loginData, ...this.deviceService.getDeviceInfo() } as LoginRequest;
    // L4: una sola llamada a /auth/login/user (antes eran dos: login + token).
    // La respuesta es { token: TokenResponse, user }; se usa response.token.
    return this.loginControllerService.loginControllerLoginWithUser(body).pipe(
      switchMap((response: any) =>
        this.onLoginSuccess({
          ...response.token,
          ...loginData,
          remember,
        })
      )
    );
  }

  /**
   * Fase 7 — login sin contraseña. El backend responde lo mismo que
   * `/auth/login/user` ({ token, user }), así que las credenciales quedan con la
   * misma forma que en el login con contraseña.
   */
  loginWithPasskey(remember: string, useBrowserAutofill = false): Observable<any> {
    return from(this.passkeyService.authenticate(useBrowserAutofill)).pipe(
      switchMap((response) =>
        this.onLoginSuccess({
          ...response.token,
          username: response.user?.['email'] ?? response.user?.['username'],
          client_id: this.runtimeConfigService.clientId,
          remember,
        })
      )
    );
  }

  refreshToken(refreshTokenRequest: AuthRefreshTokenRequest) {
    return this.loginControllerService.loginControllerExchangeToken(refreshTokenRequest).pipe(
      tap((tokens) => {
        this.credentialsService.updateCredentials(tokens);
      }),
      // R2: si el refresh falla, limpiar la sesión y propagar el error (antes se
      // devolvía of(error), que se trataba como éxito y dejaba la sesión rota).
      catchError((error) => {
        this.credentialsService.setCredentials();
        return throwError(() => error);
      })
    );
  }

  private onLoginSuccess = ({ password, ...credentials }: any) => {
    this.credentialsService.setCredentials(credentials);
    return of(credentials);
  };

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    const accessToken = this.credentialsService.credentials?.accessToken;
    const refreshToken = this.credentialsService.credentials?.refreshToken;
    // O1: mandar el refreshToken para que el backend lo revoque en el logout.
    return this.logoutControllerService
      .logoutControllerLogout(accessToken, { refreshToken })
      .pipe(finalize(() => this.credentialsService.setCredentials()));
  }

  cleanCredentials = () => this.credentialsService.setCredentials();
}
